import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import AvailabilityStatus from '@bibliocommons/availability-status';
import { ChevronLink } from '@bibliocommons/base-links';
import { bibShape, catalogBibShape } from '@bibliocommons/bc-prop-types';
import FormatIcon from '@bibliocommons/format-icon';

import AvailabilityDetailsButton from 'app/components/bibs/shared/AvailabilityDetailsButton';
import { ShelfActionsButtonContainer } from 'app/components/bibs/shared/ShelfActionsButton';
import { TitleTransactionButtonContainer } from 'app/components/bibs/shared/TitleTransactionButton';
import HoldCounts from 'app/components/shared/hold-counts';

import './AlternativeFormatBib.scss';

const AlternativeFormatBib = ({
  bib,
  catalogBib,
  onShowAvailabilityClick,
  trackDigitalResourceClick,
  trackItemTitleClick,
  trackAvailabilityClick
}) => {
  const intl = useIntl();
  const availability = bib.get('availability') ?? Immutable.Map();
  const eResourceUrl = availability.get('eresourceUrl');
  const formatId = bib.getIn(['briefInfo', 'format']) || catalogBib.getIn(['briefInfo', 'format']);
  const isHoldable = bib.getIn(['policy', 'holdable']);
  const isShowAvailability = !eResourceUrl && isHoldable;
  const metadataId = bib.get('id') || catalogBib.get('metadataId');
  const publicationDate = catalogBib.getIn(['brief', 'publicationDate']);
  const formatMessageText = intl.formatMessage({ id: `FORMAT.${formatId.toLowerCase()}` });
  const linkText = publicationDate ? `${formatMessageText}, ${publicationDate}` : formatMessageText;
  const eResourceLinkText =
    availability.get('eresourceDescription') || intl.formatMessage({ id: 'access_online_resource' });

  return (
    <div className="cp-alternative-format-bib" data-metadata-id={metadataId}>
      <div className="alternative-format-bib__top">
        <FormatIcon format={formatId} />
        <div>
          <a
            className="alternative-format-bib__format"
            href={`/v2/record/${metadataId}`}
            onClick={() => trackItemTitleClick({ linkText })}
          >
            <FormattedMessage id={`FORMAT.${formatId.toLowerCase()}`} />
            {publicationDate ? `, ${publicationDate}` : ''}
          </a>

          {isShowAvailability && (
            <>
              <div className="alternative-format-bib__availability">
                <AvailabilityStatus
                  availabilityLocationType={availability.get('availabilityLocationType')}
                  rollUpStatus={availability.get('status')}
                  status={availability.get('localisedStatus')}
                />
                <AvailabilityDetailsButton
                  availability={availability}
                  bib={bib}
                  handleButtonClick={onShowAvailabilityClick}
                  trackAvailabilityClick={trackAvailabilityClick}
                  intl={intl}
                />
              </div>
              <div className="hold-counts">
                <HoldCounts availability={availability} />
              </div>
            </>
          )}
          {eResourceUrl && (
            <ChevronLink
              className="eresource-link"
              dataKey="eresource-link"
              href={eResourceUrl}
              target="_blank"
              handleClick={() => trackDigitalResourceClick({ linkText: eResourceLinkText, url: eResourceUrl })}
            >
              {eResourceLinkText}
            </ChevronLink>
          )}
        </div>
      </div>

      <div className="alternative-format-bib__bottom">
        {isHoldable ? (
          <TitleTransactionButtonContainer metadataId={metadataId} />
        ) : (
          <FormattedMessage id="title_not_holdable" />
        )}
        <ShelfActionsButtonContainer metadataId={metadataId} right iconOnly />
      </div>
    </div>
  );
};

AlternativeFormatBib.propTypes = {
  bib: bibShape.isRequired,
  catalogBib: catalogBibShape.isRequired,
  trackDigitalResourceClick: PropTypes.func.isRequired,
  trackItemTitleClick: PropTypes.func.isRequired,
  trackAvailabilityClick: PropTypes.func.isRequired,
  onShowAvailabilityClick: PropTypes.func
};

export default AlternativeFormatBib;
